import { BRICK_COLOR_HEX } from '../engine/robloxValues'
import { AVATAR_ID, Vec3 } from '../types'
import { FRAME_SECONDS } from './constants'
import { snapshotAndConceal } from './primitives/serverStorageTemplate'
import { findStateByName, spawnProgrammedClone } from './sharedHelpers'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS06（だるまさんがころんだ）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS06.rbxl）のギミックを移植する:
 * - ServerScriptService/CreateScript: プレイ開始時に ServerStorage の
 *   ②ブロック を 10×7 = 70 枚クローンし、(10i - 50, 0, -10j - 1) の格子状に
 *   敷き詰めて床を作る。クローンには学習者が ②ブロック に付けたプログラムを
 *   起動する（Roblox の Clone() が配下の Script ごと複製されるのと同じ）。
 * - ServerScriptService/GameManager: ①おに が存在する間、毎フレーム監視する。
 *   おにが赤（BrickColor.Red()）の間にプレイヤーが動いたら
 *   （速度 > 0.5 studs/秒）体力を 0 にする。おにが消えたらループを抜けて
 *   ClearEvent → 「ゲームクリア！」を表示する（StarterGui/GUILocalScript）。
 *   おにを赤⇔他の色に切り替えるのは学習者の ①おに のプログラム。
 *
 * プレビューでの近似・意図的な差異:
 * - プレイヤーの速度（Lua: HumanoidRootPart.AssemblyLinearVelocity.Magnitude）
 *   は World に速度 API が無いため、アバター位置のフレーム間差分から近似する
 *   （落下・ジャンプ中も動いた扱いになるのは実機と同じ）。体力 0（死亡演出
 *   〜リスポーン）の間は計測をリセットし、復帰テレポートの瞬間移動を移動と
 *   誤検出しない。
 * - おにの「赤」は looks_setBrickColorTo の Red（BrickColor.Red() と同じ
 *   #C4281C）との一致で判定する（実機の BrickColor 等値比較に対応）。
 * - クリア表示は ScreenGui/TextLabel の代わりに HUD テキストで再現する
 *   （実測値: オレンジ帯・白文字・白枠 5px）。クリア時の効果音は実機にも無い。
 * - BGM はシーン JSON の bgm が再生する（ビヘイビア対象外）。実機の
 *   'Arcade Weekend' はプロダクト指定の Sunrise（bgm-overrides.json）へ
 *   差し替え済み。
 *
 * 未対応:
 * - なし（③じめん・④ボール・⑤スタートちてん・おにの色変化と Sound は
 *   学習者のプログラム対象であり、環境ギミックではない）。
 */

/** おに（色を監視する対象）のオブジェクト名。 */
const ONI_NAME = '①おに'

/** ServerStorage のブロックテンプレートのオブジェクト名。 */
const BLOCK_TEMPLATE_NAME = '②ブロック'

/** ブロック格子の枚数（Lua: i = 1..10, j = 1..7）。 */
const GRID_COUNT_X = 10
const GRID_COUNT_Z = 7

/** 許される揺れの大きさ（Lua: MOVE_THRESHOLD = 0.5 studs/秒）。 */
const MOVE_THRESHOLD_STUDS_PER_SECOND = 0.5

/**
 * おにの「赤」（Lua: BrickColor.Red() = Bright red）。学習者の
 * looks_setBrickColorTo（Red）が設定する色と同じ。
 */
const ONI_RED_HEX = BRICK_COLOR_HEX.Red.toUpperCase()

/** クリア表示の文言（StarterGui/ScreenGui/TextLabel の実測 Text）。 */
const CLEAR_TEXT = 'ゲームクリア！'

/** クリア表示のデザイン（ScreenGui/TextLabel の実測値）。 */
const CLEAR_HUD_STYLE: HudStyle = {
  background: '#FFAA00',
  color: '#FFFFFF',
  border: '#FFFFFF',
  borderWidth: 5,
  wide: true,
}

/** 2 点間の距離（studs）。 */
const distance = (a: Vec3, b: Vec3): number => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** RJS06 の環境ビヘイビアを生成する（プレイ開始毎に呼ばれる）。 */
export const createRjs06Behaviors = (): MissionBehaviors => {
  /**
   * ブロック格子の生成（Lua: ServerScriptService/CreateScript）。
   * 実機の ServerStorage 同様、テンプレート本体はプレイ中隠し、クローンに
   * だけ学習者のプログラムを起動する。
   */
  const spawnBlockGrid: BehaviorHandler = (ctx) => {
    const { world } = ctx
    // クローンの複製元はこの時点のテンプレート状態（実機の ServerStorage に
    // 保存された状態に相当）。
    const snapshot = snapshotAndConceal(world, [BLOCK_TEMPLATE_NAME]).get(
      BLOCK_TEMPLATE_NAME,
    )
    if (!snapshot) {
      return
    }
    for (let i = 1; i <= GRID_COUNT_X; i += 1) {
      for (let j = 1; j <= GRID_COUNT_Z; j += 1) {
        // Lua: clone.Position = Vector3.new(i * 10 - 50, 0, j * -10 - 1)
        spawnProgrammedClone(ctx, snapshot, 'rjs06_block', {
          x: i * 10 - 50,
          y: 0,
          z: j * -10 - 1,
        })
      }
    }
  }

  /**
   * おにの監視ループ（Lua: ServerScriptService/GameManager +
   * StarterGui/GUILocalScript の統合）。赤の間に動いたプレイヤーをキルし、
   * おにが消えたらクリア表示を出して終わる。
   */
  const watchOni: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const initial = findStateByName(world, ONI_NAME)
    if (!initial) {
      // Lua: workspace["①おに"] が無いと GameManager 自体が動かない。
      return
    }
    const oniId = initial.id
    // 直前フレームのアバター位置（速度近似用）。null は計測リセット。
    let lastPosition: Vec3 | null = null
    while (!ctx.signal.aborted) {
      // Lua: while oni and oni.Parent → 消えたらループを抜けて ClearEvent。
      if (!world.exists(oniId)) {
        ctx.setHudText?.(CLEAR_TEXT, CLEAR_HUD_STYLE)
        return
      }
      const oni = world.getState().get(oniId)
      const avatar = world.getState().get(AVATAR_ID)
      // Lua: humanoid.Health > 0 のプレイヤーだけがキル対象。死亡〜リスポーン
      // 中は位置が凍結→spawn へ瞬間移動するため、計測をリセットして
      // テレポートを移動と誤検出しない。
      if (!avatar || world.getAvatarHealth() <= 0) {
        lastPosition = null
      } else {
        // 速度はフレーム間の位置差分から近似する。赤以外の間も常に追跡し、
        // 赤に変わった瞬間のフレームから直前の動きを判定できるようにする
        // （Lua の AssemblyLinearVelocity は瞬時値のため）。
        if (
          lastPosition !== null &&
          oni &&
          oni.color.toUpperCase() === ONI_RED_HEX
        ) {
          const speed = distance(avatar.position, lastPosition) / FRAME_SECONDS
          if (speed > MOVE_THRESHOLD_STUDS_PER_SECOND) {
            world.setAvatarHealth(0)
          }
        }
        lastPosition = { ...avatar.position }
      }
      // Lua: wait()（約 1/30 秒）。
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return {
    onPlay: [spawnBlockGrid, watchOni],
    // ②ブロック は実機では ServerStorage（Script 非実行）。本体では
    // プログラムを起動せず、クローンにだけ startObjectPrograms で起動する。
    serverStorageTemplates: [BLOCK_TEMPLATE_NAME],
  }
}
